import { deriveRoute } from "./branching.js";
export function validateQuizDefinition(quiz) {
    const errors = [];
    const questionIds = new Set();
    const resultIds = new Set(quiz.results.map((result) => result.id));
    const categories = new Set(quiz.scoring.categories);
    const stageIds = quiz.progress.mode === "stages" ? new Set(quiz.progress.stages.map((stage) => stage.id)) : undefined;
    for (const question of quiz.questions) {
        if (questionIds.has(question.id))
            errors.push(`Duplicate question id "${question.id}"`);
        questionIds.add(question.id);
        validateQuestion(question, errors, categories, stageIds);
    }
    if (!questionIds.has(quiz.startQuestionId))
        errors.push(`Start question "${quiz.startQuestionId}" does not exist`);
    for (const question of quiz.questions) {
        for (const answer of question.answers) {
            if (answer.next && answer.next !== "__end__" && !questionIds.has(answer.next)) {
                errors.push(`Question "${question.id}" answer "${answer.id}" points to missing question "${answer.next}"`);
            }
        }
    }
    for (const rule of quiz.navigationRules ?? []) {
        if (!questionIds.has(rule.from))
            errors.push(`Navigation rule "${rule.id}" starts from missing question "${rule.from}"`);
        if (rule.next !== "__end__" && !questionIds.has(rule.next)) {
            errors.push(`Navigation rule "${rule.id}" points to missing question "${rule.next}"`);
        }
        validateCondition(rule.when, errors, questionIds, categories, quiz, `navigation rule "${rule.id}"`);
    }
    for (const rule of quiz.scoring.rules ?? []) {
        validateCondition(rule.when, errors, questionIds, categories, quiz, `result rule "${rule.id}"`);
        if (rule.effect.type === "override" && !resultIds.has(rule.effect.resultId)) {
            errors.push(`Rule "${rule.id}" overrides to missing result "${rule.effect.resultId}"`);
        }
        if (rule.effect.type === "restrict") {
            for (const resultId of rule.effect.resultIds) {
                if (!resultIds.has(resultId))
                    errors.push(`Rule "${rule.id}" restricts to missing result "${resultId}"`);
            }
        }
        if ((rule.effect.type === "boost" || rule.effect.type === "reduce") && !categories.has(rule.effect.category)) {
            errors.push(`Rule "${rule.id}" references missing score category "${rule.effect.category}"`);
        }
    }
    for (const result of quiz.results) {
        if (!categories.has(result.category))
            errors.push(`Result "${result.id}" references missing category "${result.category}"`);
    }
    if (quiz.personalizedResult?.mode === "answer-observations") {
        if (quiz.personalizedResult.genderQuestionId && !questionIds.has(quiz.personalizedResult.genderQuestionId)) {
            errors.push(`Personalized result references missing gender question "${quiz.personalizedResult.genderQuestionId}"`);
        }
    }
    try {
        deriveRoute(quiz, {});
        validateRouteTermination(quiz, errors);
    }
    catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
    }
    return { valid: errors.length === 0, errors };
}
export function assertValidQuizDefinition(quiz) {
    const result = validateQuizDefinition(quiz);
    if (!result.valid) {
        throw new Error(`Invalid quiz "${quiz.slug}":\n${result.errors.join("\n")}`);
    }
}
function validateQuestion(question, errors, categories, stageIds) {
    const answerIds = new Set();
    if (stageIds && (!question.stage || !stageIds.has(question.stage))) {
        errors.push(`Question "${question.id}" references missing stage "${question.stage ?? "(none)"}"`);
    }
    for (const answer of question.answers) {
        if (answerIds.has(answer.id))
            errors.push(`Duplicate answer id "${answer.id}" in question "${question.id}"`);
        answerIds.add(answer.id);
        for (const category of Object.keys(answer.scores ?? {})) {
            if (!categories.has(category)) {
                errors.push(`Answer "${question.id}.${answer.id}" references missing score category "${category}"`);
            }
        }
    }
}
function validateCondition(condition, errors, questionIds, categories, quiz, owner) {
    switch (condition.type) {
        case "answerEquals":
            validateAnswerReference(condition.questionId, condition.answerId, errors, questionIds, quiz, owner);
            break;
        case "answerIn":
            for (const answerId of condition.answerIds)
                validateAnswerReference(condition.questionId, answerId, errors, questionIds, quiz, owner);
            break;
        case "scoreAtLeast":
        case "scoreBelow":
            if (!categories.has(condition.category))
                errors.push(`${owner} references missing score category "${condition.category}"`);
            break;
        case "all":
        case "any":
            for (const child of condition.conditions)
                validateCondition(child, errors, questionIds, categories, quiz, owner);
            break;
        case "not":
            validateCondition(condition.condition, errors, questionIds, categories, quiz, owner);
            break;
    }
}
function validateAnswerReference(questionId, answerId, errors, questionIds, quiz, owner) {
    if (!questionIds.has(questionId)) {
        errors.push(`${owner} references missing question "${questionId}"`);
        return;
    }
    const question = quiz.questions.find((candidate) => candidate.id === questionId);
    if (!question?.answers.some((answer) => answer.id === answerId)) {
        errors.push(`${owner} references missing answer "${questionId}.${answerId}"`);
    }
}
function validateRouteTermination(quiz, errors) {
    const questionIds = new Set(quiz.questions.map((question) => question.id));
    for (const question of quiz.questions) {
        const stack = new Set();
        let current = question.id;
        while (current && current !== "__end__") {
            if (stack.has(current)) {
                errors.push(`Obvious navigation loop can start at "${question.id}"`);
                break;
            }
            stack.add(current);
            const currentQuestion = quiz.questions.find((candidate) => candidate.id === current);
            const firstNext = currentQuestion?.answers.find((answer) => answer.next)?.next;
            if (!firstNext)
                break;
            if (firstNext !== "__end__" && !questionIds.has(firstNext)) {
                errors.push(`Question "${current}" points to missing question "${firstNext}"`);
                break;
            }
            current = firstNext;
        }
    }
}
